import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { FadeIn, FadeInLeft, FadeInRight } from '../components/motion/FadeIn';
import { SectionLabel } from '../components/shared/SectionLabel';
import { CTASection } from '../components/shared/CTASection';
import { IMAGES } from '../lib/images';
import { ArrowRight, Phone, Mail, MapPin, Calendar } from 'lucide-react';

const HeroSection = () => (
  <section className="relative overflow-hidden pt-20 sm:pt-24 pb-14 sm:pb-16">
    <div className="absolute inset-0 -z-10" style={{ background: 'radial-gradient(1200px circle at 30% 10%, rgba(242,107,91,0.10), transparent 55%)' }} />
    <div className="mx-auto max-w-[var(--grid-max)] px-4 sm:px-6 lg:px-8">
      <div className="grid gap-10 lg:grid-cols-2 items-center">
        <FadeInLeft>
          <SectionLabel>Book a Demo</SectionLabel>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-[hsl(var(--sss-navy))]">Let's Talk About Recovering Your Lost Cases</h1>
          <p className="mt-5 text-base md:text-lg text-[hsl(var(--sss-ink))]/70">Whether you want a quick demo, have questions about the platform, or are ready to sign up — pick a time that works for you.</p>
        </FadeInLeft>
        <FadeInRight>
          <div className="rounded-2xl overflow-hidden shadow-[var(--shadow-lg)] h-56 sm:h-72">
            <img src={IMAGES.dentistTablet} alt="Dentist with patient" className="w-full h-full object-cover" loading="lazy" />
          </div>
        </FadeInRight>
      </div>
    </div>
  </section>
);

const CalendlySection = () => {
  useEffect(() => {
    // Load Calendly widget script
    const script = document.createElement('script');
    script.src = 'https://assets.calendly.com/assets/external/widget.js';
    script.async = true;
    document.body.appendChild(script);
    return () => {
      document.body.removeChild(script);
    };
  }, []);

  return (
    <section className="py-16 sm:py-20">
      <div className="mx-auto max-w-[var(--grid-max)] px-4 sm:px-6 lg:px-8">
        <div className="grid gap-10 lg:grid-cols-12 items-start">
          {/* Calendly Embed */}
          <FadeInLeft className="lg:col-span-8">
            <Card className="rounded-2xl border border-[hsl(var(--border))] bg-white shadow-[var(--shadow-md)] overflow-hidden">
              <CardContent className="p-0">
                <div
                  className="calendly-inline-widget"
                  data-url="https://calendly.com/smilescoresolutions/30min?hide_event_type_details=1&hide_gdpr_banner=1"
                  style={{ minWidth: '320px', height: '700px' }}
                />
              </CardContent>
            </Card>
          </FadeInLeft>

          {/* Quick Actions */}
          <FadeInRight className="lg:col-span-4">
            <div className="space-y-6">
              <Card className="rounded-2xl border border-[hsl(var(--border))] bg-white shadow-[var(--shadow-sm)]">
                <CardContent className="p-6">
                  <h3 className="text-base font-semibold text-[hsl(var(--sss-navy))] mb-2">Ready to get started?</h3>
                  <p className="text-sm text-muted-foreground mb-4">Skip the demo and sign up now.</p>
                  <a href="https://approvemysmile.com/register/personal" target="_blank" rel="noopener noreferrer">
                    <Button className="w-full bg-[hsl(var(--sss-coral))] hover:bg-[hsl(var(--sss-coral-2))] text-white rounded-xl h-11">Get Started for $249/mo <ArrowRight className="ml-2 h-4 w-4" /></Button>
                  </a>
                </CardContent>
              </Card>
              <Card className="rounded-2xl border border-[hsl(var(--border))] bg-white shadow-[var(--shadow-sm)]">
                <CardContent className="p-6">
                  <h3 className="text-base font-semibold text-[hsl(var(--sss-navy))] mb-4">Contact Info</h3>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3 text-sm text-[hsl(var(--sss-ink))]/80"><Mail className="h-4 w-4 text-[hsl(var(--sss-teal))]" /><span>arthur@smilescoresolutions.com</span></div>
                    <div className="flex items-start gap-3 text-sm text-[hsl(var(--sss-ink))]/80"><MapPin className="h-4 w-4 text-[hsl(var(--sss-teal))] mt-0.5" /><span>1234 West Chester Pike Ste. 300<br />West Chester, PA 19382</span></div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </FadeInRight>
        </div>
      </div>
    </section>
  );
};

const FAQSection = () => {
  const faqs = [
    { q: 'What is Smile Score Solutions?', a: 'Smile Score Solutions is a practice growth platform that helps dental offices recover patients who are declined for third-party financing. We give declined patients access to a practice-branded portal with credit monitoring, guided improvement tools, and automated nurture — so they can work toward financial readiness and return to your practice when they qualify.' },
    { q: 'How does this benefit my dental practice?', a: "It recovers revenue you're currently losing. When patients are declined for financing, most practices have no follow-up system. Smile Score Solutions keeps those patients in your ecosystem and alerts your team when they're ready." },
    { q: 'Is this a credit repair service?', a: 'No. Smile Score Solutions is a financial wellness and education platform. We provide credit monitoring from top bureaus, personalized guided action steps, and tools that empower patients to understand their credit profile and make informed decisions. We do not repair credit on behalf of patients.' },
    { q: 'What does the patient experience look like?', a: 'Patients access a portal that is branded to your practice. Inside, they get a personalized improvement plan, credit monitoring, educational resources, and motivational communications.' },
    { q: 'Is there a cost to patients?', a: 'No. The platform is provided to patients at no charge. Your practice pays $249/month for unlimited patient enrollments.' },
    { q: 'How much does it cost?', a: '$249 per month, flat. Unlimited patients. No per-patient fees. No long-term contracts. Cancel anytime.' },
    { q: 'How long does it take for patients to see results?', a: 'Patients learn proven, established strategies like disputing inaccurate items, building positive credit history, and managing debt that they can apply to their own situation. Results vary, depend on each patient\'s individual financial circumstances, and are never guaranteed.' },
    { q: 'How do I enroll patients?', a: 'You can send patients a link via text or email, invite them from inside the dashboard, or integrate with your CRM for automated enrollment.' },
    { q: 'Does it integrate with my existing systems?', a: 'Yes. We offer API access and automation support to connect with your existing marketing tools, CRM, or patient management system.' },
    { q: 'How do I get started?', a: 'You can sign up immediately at our registration page or book a demo to see the platform in action first.' },
  ];
  return (
    <section className="py-16 sm:py-20 bg-[hsl(var(--secondary))]">
      <div className="mx-auto max-w-[var(--grid-max)] px-4 sm:px-6 lg:px-8 max-w-3xl">
        <FadeIn>
          <SectionLabel>FAQ</SectionLabel>
          <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight text-[hsl(var(--sss-navy))] mb-8">Frequently Asked Questions</h2>
          <Accordion type="single" collapsible data-testid="faq-accordion" className="space-y-3">
            {faqs.map((faq, i) => (
              <AccordionItem key={i} value={`faq-${i}`} data-testid="faq-item" className="rounded-2xl border border-[hsl(var(--border))] bg-white px-4 shadow-[var(--shadow-sm)]">
                <AccordionTrigger className="text-left text-sm sm:text-base font-medium text-[hsl(var(--sss-navy))] hover:no-underline py-4">{faq.q}</AccordionTrigger>
                <AccordionContent className="text-sm text-muted-foreground pb-4 leading-relaxed">{faq.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </FadeIn>
      </div>
    </section>
  );
};

export default function ContactPage() {
  return (
    <div>
      <HeroSection />
      <CalendlySection />
      <FAQSection />
      <CTASection headline="Ready to Recover Your Lost Cases?" subheadline="Get started with Smile Score Solutions today and stop leaving revenue on the table." />
    </div>
  );
}
